import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../../hooks/useLanguage";
import { POST_USER_DATA, TOGGLE_DIALOG } from "../../../store/action";
import { EmailValidation } from "../../functions/validateEmailPassword";
import { openNotificationWithIcon } from "../notification-bar";
import { t } from "../../../translations";
import { POST } from "../../constants/request-type";
import { service_api } from "../../constants/url";
import { MakeRequestAsync } from "../../functions/axios";
import { decryptSingleData } from "../../functions/cryptojs";
import { storeUserData } from "../../../utils/authStorageUtils";
import { signInWithApple, signInWithGoogle } from "../../../utils/firebaseOAuth";
import { getFCMToken } from "../../../utils/firebaseMessaging";
import { createLogger } from "../../../utils/logger";
import {
  RESET_PASSWORD_COOLDOWN_MS,
  RESET_PASSWORD_COOLDOWN_KEY
} from "./constants";
import { validatePassword, validatePhoneNumber } from "./utils";

const logger = createLogger("LoginSignupDialog");

const post_user = (data) => ({ type: POST_USER_DATA, data });
const toggle_dialog = (data) => ({ type: TOGGLE_DIALOG, data });

export const useLoginSignup = (open, onClose) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [showOtpStep, setShowOtpStep] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [i_exist, setIexist] = useState("");
  const [load, setLoad] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordCooldown, setResetPasswordCooldown] = useState(false);
  const [errorLog, setErrorLog] = useState("");
  const [activeTab, setActiveTab] = useState("email"); // "email" | "sms"
  const [isSignUp, setIsSignUp] = useState(false);

  const { selectedLang } = useLanguage();
  const location = useLocation();
  const dispatch = useDispatch();
  const resetCooldownTimeout = useRef(null);

  const setOpenDialog = useCallback(
    (data) => dispatch(toggle_dialog(data)),
    [dispatch]
  );

  const post_user_data = useCallback(
    (data) => dispatch(post_user(data)),
    [dispatch]
  );

  const clearResetCooldownTimeout = useCallback(() => {
    if (resetCooldownTimeout.current) {
      clearTimeout(resetCooldownTimeout.current);
      resetCooldownTimeout.current = null;
    }
  }, []);

  const scheduleResetCooldown = useCallback(
    (durationMs, { persist = true } = {}) => {
      setResetPasswordCooldown(true);
      clearResetCooldownTimeout();
      if (persist) {
        sessionStorage.setItem(
          RESET_PASSWORD_COOLDOWN_KEY,
          (Date.now() + durationMs).toString()
        );
      }
      resetCooldownTimeout.current = setTimeout(() => {
        setResetPasswordCooldown(false);
        sessionStorage.removeItem(RESET_PASSWORD_COOLDOWN_KEY);
      }, durationMs);
    },
    [clearResetCooldownTimeout]
  );

  const resetDialogState = useCallback(() => {
    setLoad(false);
    setResetPasswordLoading(false);
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setFirstname("");
    setLastname("");
    setPhoneNumber("");
    setOtp("");
    setShowOtpStep(false);
    setOtpLoading(false);
    setIexist(false);
    setErrorLog("");
    setActiveTab("email");
    setIsSignUp(false);
  }, []);


  const sendOtp = useCallback(
    async (phoneValue) => {
      const phone = phoneValue != null ? String(phoneValue).replace(/\D/g, "") : phoneNumber;
      if (!phone) {
        return openNotificationWithIcon("error", t("navbar.phone_enter", selectedLang));
      }
      if (!validatePhoneNumber(phone)) {
        return openNotificationWithIcon("error", t("navbar.phone_invalid", selectedLang));
      }
      setPhoneNumber(phone);
      setOtpLoading(true);
      try {
        await MakeRequestAsync({
          type: POST,
          url: service_api,
          route: "user/send-user-otp",
          data: { email: "", phoneNumber: phone },
          token: null
        });
        setShowOtpStep(true);
        openNotificationWithIcon("success", t("navbar.otp_sent", selectedLang));
      } catch (err) {
        setErrorLog(err?.response?.data?.message);
        openNotificationWithIcon(
          "error",
          err?.response?.data?.message || t("navbar.otp_failed_send", selectedLang)
        );
      } finally {
        setOtpLoading(false);
        setLoad(false);
      }
    },
    [phoneNumber, selectedLang]
  );

  const verifyOtp = useCallback(async () => {
    if (!otp) {
      return openNotificationWithIcon("error", t("navbar.otp_enter_required", selectedLang));
    }
    setOtpLoading(true);
    const pushToken = await getFCMToken();
    try {
      const response = await MakeRequestAsync({
        type: POST,
        url: service_api,
        route: "user/verify-user-otp",
        data: { email, phoneNumber, otp, ...(pushToken && { pushToken }) },
        token: null
      });
      setOtpLoading(false);
      const decrypt = decryptSingleData(response?.data?.user);
      const finalUserData = {
        ...decrypt,
        refresh_token: response?.data?.auth_token || decrypt?.refresh_token
      };
      post_user_data(finalUserData);
      storeUserData(finalUserData);
      setOpenDialog(false);
      onClose();
    } catch (err) {
      setOtpLoading(false);
      setErrorLog(err?.response?.data?.message);
      openNotificationWithIcon(
        "error",
        err?.response?.data?.message || t("navbar.otp_invalid", selectedLang)
      );
    }
  }, [email, phoneNumber, otp, post_user_data, setOpenDialog, onClose, selectedLang]);

  const handlePasswordSignup = useCallback(async () => {
    try {
      const response = await MakeRequestAsync({
        type: POST,
        url: service_api,
        route: "user/create-user",
        data: { email, password, firstname, lastname, number: phoneNumber },
        token: null
      });
      const decrypt = decryptSingleData(response?.data?.user);
      post_user_data(decrypt);
      setOpenDialog(false);
      onClose();
    } catch (err) {
      setErrorLog(err?.response?.data?.message);
      openNotificationWithIcon("error", err?.response?.data?.message);
    } finally {
      setLoad(false);
    }
  }, [email, password, firstname, lastname, phoneNumber, post_user_data, setOpenDialog, onClose]);

  const handlePasswordLogin = useCallback(async () => {
    const pushToken = await getFCMToken();
    try {
      const response = await MakeRequestAsync({
        type: POST,
        url: service_api,
        route: "user/login-user",
        data: { email, password, ...(pushToken && { pushToken }) },
        token: null
      });
      const decrypt = decryptSingleData(response?.data?.user);
      const finalUserData = {
        ...decrypt,
        refresh_token: response?.data?.auth_token || decrypt?.refresh_token
      };
      post_user_data(finalUserData);
      storeUserData(finalUserData);
      setOpenDialog(false);
      onClose();
    } catch (err) {
      setErrorLog(err?.response?.data?.message);
      openNotificationWithIcon("error", err?.response?.data?.message);
    } finally {
      setLoad(false);
    }
  }, [email, password, post_user_data, setOpenDialog, onClose]);

  const handlePasswordReset = useCallback(async () => {
    if (!EmailValidation(email)) {
      const message = t("navbar.wrong_email_notification", selectedLang);
      setErrorLog(message);
      openNotificationWithIcon("error", message);
      return;
    }
    if (resetPasswordCooldown) {
      openNotificationWithIcon("warning", t("navbar.reset_password_wait", selectedLang));
      return;
    }
    setResetPasswordLoading(true);
    try {
      await MakeRequestAsync({
        type: POST,
        url: service_api,
        route: "user/reset-password",
        data: { email, lang: selectedLang }
      });
      openNotificationWithIcon("success", t("navbar.reset_password_sent", selectedLang));
      scheduleResetCooldown(RESET_PASSWORD_COOLDOWN_MS);
    } catch (err) {
      openNotificationWithIcon(
        "error",
        err?.response?.data?.message || t("navbar.reset_password_failed", selectedLang)
      );
    } finally {
      setResetPasswordLoading(false);
    }
  }, [email, resetPasswordCooldown, selectedLang, scheduleResetCooldown]);

  const handleGoogleLogin = useCallback(async () => {
    try {
      setLoad(true);
      const userInfo = await signInWithGoogle();
      if (!userInfo) return;
      const pushToken = await getFCMToken();
      const response = await MakeRequestAsync({
        type: POST,
        url: service_api,
        route: "user/login-oauth-user",
        data: {
          email: userInfo?.email,
          firstname: userInfo?.given_name,
          lastname: userInfo?.family_name,
          ...(pushToken && { pushToken })
        },
        token: null
      });
      const decrypt = decryptSingleData(response?.data?.user);
      const finalUserData = {
        ...decrypt,
        refresh_token: response?.data?.auth_token || decrypt?.refresh_token
      };
      post_user_data(finalUserData);
      storeUserData(finalUserData);
      setOpenDialog(false);
      onClose();
    } catch (err) {
      logger.error("Google login failed:", err);
      openNotificationWithIcon("error", t("navbar.error_google", selectedLang));
    } finally {
      setLoad(false);
    }
  }, [post_user_data, setOpenDialog, onClose, selectedLang]);

  const handleAppleLogin = useCallback(async () => {
    try {
      setLoad(true);
      const userInfo = await signInWithApple();
      if (!userInfo) return;
      const pushToken = await getFCMToken();
      const response = await MakeRequestAsync({
        type: POST,
        url: service_api,
        route: "user/login-oauth-user",
        data: {
          email: userInfo?.email,
          firstname: userInfo?.given_name,
          lastname: userInfo?.family_name,
          ...(pushToken && { pushToken })
        },
        token: null
      });
      const decrypt = decryptSingleData(response?.data?.user);
      const finalUserData = {
        ...decrypt,
        refresh_token: response?.data?.auth_token || decrypt?.refresh_token
      };
      post_user_data(finalUserData);
      storeUserData(finalUserData);
      setOpenDialog(false);
      onClose();
    } catch (err) {
      logger.error("Apple login failed:", err);
      openNotificationWithIcon("error", t("navbar.error_apple", selectedLang));
    } finally {
      setLoad(false);
    }
  }, [post_user_data, setOpenDialog, onClose, selectedLang]);

  useEffect(() => {
    if (open && location.pathname === "/reset-password") {
      setOpenDialog(false);
      resetDialogState();
    }
  }, [location.pathname, open, setOpenDialog, resetDialogState]);

  useEffect(() => {
    const cooldownUntil = sessionStorage.getItem(RESET_PASSWORD_COOLDOWN_KEY);
    if (cooldownUntil) {
      const remainingTime = Number(cooldownUntil) - Date.now();
      if (remainingTime > 0) {
        scheduleResetCooldown(remainingTime, { persist: false });
      } else {
        sessionStorage.removeItem(RESET_PASSWORD_COOLDOWN_KEY);
      }
    }
    return () => clearResetCooldownTimeout();
  }, [clearResetCooldownTimeout, scheduleResetCooldown]);

  const handleClose = useCallback(() => {
    setOpenDialog(false);
    resetDialogState();
    onClose();
  }, [setOpenDialog, resetDialogState, onClose]);

  return {
    // State
    email,
    setEmail,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    firstname,
    setFirstname,
    lastname,
    setLastname,
    phoneNumber,
    setPhoneNumber,
    otp,
    setOtp,
    showOtpStep,
    setShowOtpStep,
    otpLoading,
    i_exist,
    load,
    setLoad,
    resetPasswordLoading,
    resetPasswordCooldown,
    activeTab,
    setActiveTab,
    isSignUp,
    setIsSignUp,
    errorLog,
    setErrorLog,
    selectedLang,
    // Handlers
    validatePassword,
    validatePhoneNumber,
    sendOtp,
    verifyOtp,
    handlePasswordSignup,
    handlePasswordLogin,
    handlePasswordReset,
    handleGoogleLogin,
    handleAppleLogin,
    handleClose
  };
};
